import { FlexView, Row } from '@cuemath/comps';
import { Text } from '@cuemath/web-comps';
import { Table, TableCell } from 'comps';
import React, { useCallback, useEffect, useState } from 'react';
import { Button, TableRow } from 'semantic-ui-react';
import styled from 'styled-components';

import COLORS from '../../../constants/colors';
import { useGetAllEvalSpecialistRoster } from 'actions/eval-reporting';
import AddPause from './add-pause';
import CancelPause from './cancel-pause';
import {
  Chip,
  ErrorLine,
  FilterRow,
  MonoText,
  MonthPicker,
  RegionPicker,
  PageHeader,
} from '../common/filters';
import { currentMonthValue, describeInterval, formatIstDate } from '../common/constants';

const Container = styled(FlexView)`
  padding: 0 16px;
  width: 100%;
`;
const TableWrapper = styled(Row)`
  margin-top: 16px;
  width: 100%;
`;
const PauseList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;
const PauseEntry = styled(Row)`
  gap: 8px;
  align-items: center;
`;
const CancelLink = styled.span`
  cursor: pointer;
  text-decoration: underline;
  color: ${COLORS.RUSTY_RED};
  font-size: 12px;
  white-space: nowrap;
`;

const countPausedDays = specialist => (specialist.pause_intervals || []).length;

const SpecialistRoster = () => {
  const [month, setMonth] = useState(currentMonthValue);
  const [region, setRegion] = useState(null);
  const [pausingSpecialist, setPausingSpecialist] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);

  const {
    data,
    getAll: getRoster,
    isProcessing: isLoading,
    error: rosterError,
  } = useGetAllEvalSpecialistRoster();

  const fetchRoster = useCallback(() => {
    getRoster({ month, ...(region ? { region } : {}) });
  }, [getRoster, month, region]);

  useEffect(() => {
    fetchRoster();
  }, [fetchRoster]);

  const specialists = data?.specialists || [];

  return (
    <Container>
      <PageHeader title="Specialist Roster" />
      <FilterRow justifyContent="space-between" alignItems="center">
        <FilterRow alignItems="center">
          <MonthPicker value={month} onChange={setMonth} />
          <RegionPicker value={region} onChange={setRegion} />
        </FilterRow>
        <Text variant="body3" color={COLORS.GRAY}>
          Pause times are IST. A pause consumes no leave units.
        </Text>
      </FilterRow>

      <ErrorLine error={rosterError} />

      <TableWrapper>
        <Table
          headerCell={[
            { title: 'Specialist' },
            { title: 'Specialist ID' },
            { title: 'Days paused' },
            { title: 'Pauses (IST)' },
            { title: '' },
          ]}
          dataLength={specialists.length}
          havePagination
          showInfo={false}
          showLoader={isLoading}
          flexBasis="100%"
          noDataMessage="No eval specialists on the bench for this month."
          scrollBodyHeight={isLoading || !specialists.length ? '532px' : 'calc(100vh - 210px)'}
        >
          {specialists.map(specialist => (
            <TableRow key={specialist.teacher_id}>
              <TableCell width={3} textTransform="none">
                {specialist.teacher_name || '-'}
              </TableCell>
              <TableCell width={3} textTransform="none">
                <MonoText>{specialist.teacher_id}</MonoText>
              </TableCell>
              <TableCell width={2}>{countPausedDays(specialist)}</TableCell>
              <TableCell width={6} textTransform="none">
                {countPausedDays(specialist) ? (
                  <PauseList>
                    {(specialist.pause_intervals || []).map(interval => (
                      <PauseEntry
                        key={`${interval.date}-${(interval.leave_usage_ids || []).join(',')}`}
                      >
                        <Chip color={COLORS.YELLOW_4}>
                          {formatIstDate(interval.date)} · {describeInterval(interval)}
                        </Chip>
                        {(interval.leave_usage_ids || []).length > 0 && (
                          <CancelLink
                            onClick={() => setCancelTarget({ specialist, interval })}
                          >
                            Cancel
                          </CancelLink>
                        )}
                      </PauseEntry>
                    ))}
                  </PauseList>
                ) : (
                  <Text variant="body3" color={COLORS.GRAY}>
                    Available all month
                  </Text>
                )}
              </TableCell>
              <TableCell width={2}>
                <Button
                  size="tiny"
                  color="black"
                  onClick={() => setPausingSpecialist(specialist)}
                >
                  Add pause
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </Table>
      </TableWrapper>

      {pausingSpecialist && (
        <AddPause
          specialist={pausingSpecialist}
          closeModal={() => setPausingSpecialist(null)}
          onApplied={fetchRoster}
        />
      )}

      {cancelTarget && (
        <CancelPause
          target={cancelTarget}
          closeModal={() => setCancelTarget(null)}
          onCancelled={fetchRoster}
        />
      )}
    </Container>
  );
};

export default SpecialistRoster;
